import React, { useState } from 'react';
import { ChevronRight } from 'lucide-react';

import { useCustomDispatch, useCustomNavigate } from '../../hooks';
import { useCustomSelectors } from '../../services/selectors/allSelectors';

import Cookies from 'js-cookie';
import InitatedChatBox from './initatedChatBox';

import * as routNames from '../../routes/routeName';
import { addReadMessages } from '../../services/actions/chat/chatReadUnReadMessages';

const getValidPhoto = (photo) => {
	const value = String(photo || '').trim();
	return value && !['null', 'undefined'].includes(value.toLowerCase()) ? value : '';
};

const getInitials = (value) => {
	const name = String(value || '').trim();
	if (!name) return 'U';

	const parts = name.split(/\s+/).filter(Boolean);
	if (parts.length === 1) {
		return parts[0].slice(0, 2).toUpperCase();
	}

	return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const ChatUserAvatar = ({ name, photo, showStatus = true, onlineStatus }) => {
	const [imageFailed, setImageFailed] = useState(false);
	const validPhoto = getValidPhoto(photo);
	const shouldShowImage = validPhoto && !imageFailed;

	return (
		<div className="support-chat-list-avatar">
			{shouldShowImage ? (
				<img
					src={validPhoto}
					alt=""
					className='max-w-none bg-light object-cover'
					onError={() => setImageFailed(true)}
				/>
			) : (
				<span className="support-chat-list-avatar-fallback">
					{getInitials(name)}
				</span>
			)}
			{showStatus ? (
				onlineStatus === 'online' ?
					<span className="support-chat-list-avatar-dot online"></span>
					: onlineStatus === 'offline' ?
						<span className="support-chat-list-avatar-dot offline"></span>
						: onlineStatus === 'idle' ?
							<span className="support-chat-list-avatar-dot idle"></span>
							: null
			) : null}
		</div>
	);
};

const getRecentPreview = (value) => {
	if (value?.latest_message?.message_media?.length) return 'Photo';
	if (value?.latest_message?.media_pdf?.length) return 'Attachment';
	return value?.latest_message?.message_text?.split(' ')?.slice(0, 4)?.join(' ') || 'No messages';
};

export const RecentChats = ({
	openChatbox,
	employeesOnlineStatus,
	selectedEmployeeId,
	setMyInputMessages,
	setChatAppenedMessages,
	setChatInput,
	setAllGroupMessages,
	allGroupMessages,
	chatInput,
	myInputMessages,
	chatAppenedMessages,
	openOverlay,
	closeOverlay,
	setSelectedEmployeeId,
	recentChatLoading,
	setPage,
	page,
	inline = false
}) => {

	const {
		profileData,
		recentChatList } = useCustomSelectors()

	const dispatch = useCustomDispatch()

	const recentChatsResponse = recentChatList?.data?.latest_messages

	const [chatValue, setChatValue] = useState(null);

	return (
		<>
			{(!inline || !selectedEmployeeId) && (
			<div className="support-chat-thread-list">
				{
					recentChatLoading ? <div className="support-chat-loading">Loading...</div>

						:
						recentChatsResponse?.length === 0 ?
							<div>
								<h4 className="rubik-500 fs-16 dark-color">
									No results found.
								</h4>
							</div>
							:
							recentChatList?.error !== null ?
								<div>
									<h4 className="rubik-500 fs-16 dark-color">
										Something Went wrong please try again later.
									</h4>
								</div>
								:
								recentChatsResponse?.map((value, index) => {

									return (
										<React.Fragment key={index}>
											{
												value?.user_id.length === 0 ? null :
													!value?.group_id ? null :
														!value?.name ? null :
															value?.latest_message === null ? null :
																<>
																	<button
																		type="button"
																		className={`support-chat-thread ${selectedEmployeeId === value?.user_id?.[0] ? 'is-active' : ''}`}
																		onClick={() => {
																			setChatValue(value);
																			openChatbox(value, 'recent_chat_list');
																			if (value.group_id && value.unread_count > 0) addReadMessages(value.group_id, dispatch);
																		}}
																	>
																		<div className="support-chat-thread-main">
																			<ChatUserAvatar
																				name={value?.name}
																				photo={value?.thumbnail}
																				showStatus={value?.group_type !== 'one_to_many'}
																				onlineStatus={value?.online_status}
																			/>
																			<div className="support-chat-thread-copy">
																				<div className="support-chat-thread-row">
																					<span className="support-chat-thread-title">{value?.name}</span>
																					<span className="support-chat-thread-date">{value?.latest_message?.update_time}</span>
																				</div>
																				<div className="support-chat-thread-meta-line">
																					<span className="support-chat-thread-preview">{getRecentPreview(value)}</span>
																					{value?.unread_count > 0 ? (
																						<span className="support-chat-status unread">{value?.unread_count}</span>
																					) : null}
																				</div>
																			</div>
																		</div>
																		<ChevronRight size={13} className="support-chat-thread-chevron" />
																	</button>
																</>
											}
										</React.Fragment>

									)
								})}
			</div>
			)}
			<InitatedChatBox value={chatValue}
				selectedEmployeeId={selectedEmployeeId}
				profileData={profileData}
				employeesOnlineStatus={employeesOnlineStatus}
				setMyInputMessages={setMyInputMessages}
				setChatAppenedMessages={setChatAppenedMessages}
				setChatInput={setChatInput}
				setAllGroupMessages={setAllGroupMessages}
				allGroupMessages={allGroupMessages}
				chatInput={chatInput}
				myInputMessages={myInputMessages}
				chatAppenedMessages={chatAppenedMessages}
				openOverlay={openOverlay}
				closeOverlay={closeOverlay}
				setSelectedEmployeeId={setSelectedEmployeeId}
				setPage={setPage}
				page={page}
				inline={inline}
				onBack={() => setSelectedEmployeeId(null)}
			/>
		</>
	)
}


export const ResponsiveRecentChatsView = ({
	recentChatLoading,
}) => {

	const navigate = useCustomNavigate()
	const { profileData, recentChatList } = useCustomSelectors()

	const recentChatsResponse = recentChatList?.data?.latest_messages

	return (
		<>
			<div className="support-chat-thread-list">
				{

					recentChatLoading ? <div className="support-chat-loading">Loading...</div>
						:
						recentChatsResponse?.length === 0 ?
							<div>
								<h4 className="rubik-500 fs-16 dark-color">
									No results found.
								</h4>
							</div>
							:
							recentChatList?.error !== null ?
								<div>
									<h4 className="rubik-500 fs-16 dark-color">
										Something Went wrong please try again later.
									</h4>
								</div>
								:
								recentChatsResponse?.map((value, index) => {
									return (<React.Fragment key={index}>
										{
											profileData?.user_id === value?.user_id ? null :
												<>
													<button
														type="button"
														className="support-chat-thread"
														onClick={() => { return navigate(`${routNames.CHAT_NOW}/${value.group_id}`), Cookies.remove('previous') }}
													>
														<div className="support-chat-thread-main">
															<ChatUserAvatar
																name={value?.name}
																photo={value?.thumbnail}
																onlineStatus={value?.online_status}
															/>

															<div className="support-chat-thread-copy">
																<div className="support-chat-thread-row">
																	<span className="support-chat-thread-title">{value?.name}</span>
																	<span className="support-chat-thread-date">{value?.latest_message?.update_time}</span>
																</div>
																<div className="support-chat-thread-meta-line">
																	<span className="support-chat-thread-preview">{getRecentPreview(value)}</span>
																	{value?.unread_count === '0' || value?.unread_count === 0 ? null : (
																		<span className="support-chat-status unread">{value?.unread_count}</span>
																	)}
																</div>
															</div>
														</div>
														<ChevronRight size={13} className="support-chat-thread-chevron" />
													</button>
												</>
										}
									</React.Fragment>

									)
								})}
			</div>
		</>
	)
}
